/**
 * `buildStateTimeline` — pure model for the B3.2 state timeline. Folds a
 * node's event stream into the band-and-dot data the `<StateTimeline/>` SVG
 * renders: one coloured segment per span the node spent in a given state.
 *
 * Issue #763 (Phase 3, temporal frontend). Kept pure + dependency-free (no
 * React, no DOM, no `Date.now` baked in — `now` is injected) so the timeline
 * logic is unit-testable in isolation and the rendering is a thin view.
 *
 * Brand firewall (§A9): this is about *the node's* trajectory through the four
 * factual states — never a person. No actor, no count, no rate enters this
 * model; the dots carry only "what changed", not "who" or "how many".
 *
 * The model:
 *   - filter the stream to `state_changed` events (the only ones that move the
 *     node between states);
 *   - the node starts in "planned" at its `node_created` time (or the first
 *     event's time if no create row is present);
 *   - each `state_changed` opens a new segment of the destination state;
 *   - the final segment runs to `now`.
 */

import type { NodeHistoryItem } from "../api/useNodeHistory";
import { type TaskState, taskStateStyle } from "../tree/state";

/** One coloured span the node spent in a state. */
export interface TimelineSegment {
  readonly state: TaskState;
  readonly label: string;
  /** Foreground colour (from `TASK_STATES`). */
  readonly fg: string;
  /** ISO start of this span. */
  readonly fromISO: string;
  /** ISO end of this span (`now` for the last). */
  readonly toISO: string;
  /** Fractional [0,1] start along the axis. */
  readonly fromFrac: number;
  /** Fractional [0,1] end along the axis. */
  readonly toFrac: number;
}

/** One marker on the axis (a state change, or the initial "created" point). */
export interface TimelineMarker {
  /** ISO of the marked event. */
  readonly atISO: string;
  /** Fractional [0,1] position along the axis. */
  readonly frac: number;
  /** A short factual caption shown above the dot ("created", "started", …). */
  readonly label: string;
  /** The state this marker introduces (drives the dot colour). */
  readonly state: TaskState;
  readonly fg: string;
}

export interface StateTimeline {
  readonly segments: TimelineSegment[];
  readonly markers: TimelineMarker[];
  /** Distinct tick instants for the axis (start, each change, now). */
  readonly ticks: { atISO: string; frac: number }[];
  /** Whether there is anything to draw (≥1 segment with positive width). */
  readonly hasSpan: boolean;
}

function ms(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function isTaskState(v: unknown): v is TaskState {
  return v === "planned" || v === "in_progress" || v === "blocked" || v === "done";
}

/** A short factual caption for a state-change marker (never evaluative, §A9). */
function changeCaption(state: TaskState): string {
  switch (state) {
    case "in_progress":
      return "started";
    case "done":
      return "completed";
    case "blocked":
      return "blocked";
    default:
      return "reset";
  }
}

/**
 * Fold a node's event stream into the timeline model. `nowISO` is the right
 * edge (defaults to the current instant). Events are assumed ascending by
 * `occurred_at` (the endpoint's contract); we sort defensively anyway.
 */
export function buildStateTimeline(
  events: NodeHistoryItem[],
  nowISO: string = new Date().toISOString(),
): StateTimeline {
  const sorted = [...events].sort((a, b) => ms(a.occurred_at) - ms(b.occurred_at));
  const created = sorted.find((e) => e.event_type === "node_created");
  const startISO = created?.occurred_at ?? sorted[0]?.occurred_at ?? nowISO;

  const start = ms(startISO);
  const now = Math.max(ms(nowISO), start);
  const span = now - start;
  const frac = (iso: string) => (span > 0 ? Math.min(Math.max((ms(iso) - start) / span, 0), 1) : 0);

  // The state-change points, each opening a new destination-state segment.
  const changes = sorted
    .filter((e) => e.event_type === "state_changed")
    .map((e) => {
      const payload = (e.payload ?? {}) as Record<string, unknown>;
      return { atISO: e.occurred_at, to: payload.to };
    })
    .filter((c): c is { atISO: string; to: TaskState } => isTaskState(c.to));

  // Build segments: the node starts "planned" at `startISO`, then each change
  // opens a new span of its destination state; the last span runs to `now`.
  const boundaries: { atISO: string; state: TaskState }[] = [
    { atISO: startISO, state: "planned" },
    ...changes.map((c) => ({ atISO: c.atISO, state: c.to })),
  ];

  const segments: TimelineSegment[] = boundaries.map((b, i) => {
    const next = boundaries[i + 1];
    const toISO = next ? next.atISO : nowISO;
    const style = taskStateStyle(b.state);
    return {
      state: b.state,
      label: style.label,
      fg: style.fg,
      fromISO: b.atISO,
      toISO,
      fromFrac: frac(b.atISO),
      toFrac: frac(toISO),
    };
  });

  // Markers: the "created" point, then one per state change.
  const markers: TimelineMarker[] = [
    {
      atISO: startISO,
      frac: frac(startISO),
      label: "created",
      state: "planned",
      fg: taskStateStyle("planned").fg,
    },
    ...changes.map((c) => ({
      atISO: c.atISO,
      frac: frac(c.atISO),
      label: changeCaption(c.to),
      state: c.to,
      fg: taskStateStyle(c.to).fg,
    })),
  ];

  // Ticks: start, each change, and now (deduped by instant).
  const tickInstants = [startISO, ...changes.map((c) => c.atISO), nowISO];
  const seen = new Set<number>();
  const ticks = tickInstants
    .filter((iso) => {
      const t = ms(iso);
      if (seen.has(t)) return false;
      seen.add(t);
      return true;
    })
    .map((iso) => ({ atISO: iso, frac: frac(iso) }));

  return {
    segments,
    markers,
    ticks,
    hasSpan: span > 0,
  };
}
